import type { HTMLAttributes, JSX, TargetedEvent } from "preact";

interface NumberInputProps extends Omit<
  HTMLAttributes<HTMLInputElement>,
  "type" | "value" | "onInput"
> {
  value: number | "";
  onValue: (value: number | "") => void;
  label?: string;
  tooltip?: string;
  min?: number;
  max?: number;
  step?: number;
}

const cx = (...parts: (string | false | null | undefined)[]): string =>
  parts.filter((p): p is string => typeof p === "string" && p.length > 0).join(" ");

export const NumberInput = ({
  value,
  onValue,
  label,
  tooltip,
  min,
  max,
  step,
  class: className,
  ...rest
}: NumberInputProps): JSX.Element => {
  const handleInput = (event: TargetedEvent<HTMLInputElement>): void => {
    const raw = event.currentTarget.value;
    if (raw === "") {
      onValue("");
      return;
    }
    const n = Number(raw);
    onValue(Number.isFinite(n) ? n : "");
  };

  const inputCls = cx("ww-input", typeof className === "string" ? className : undefined);

  return (
    <label>
      {label !== undefined && (
        <span class="ww-label-row">
          <span class="ww-label">{label}</span>
          {tooltip !== undefined && (
            <span class="ww-tooltip" tabIndex={0} aria-label={tooltip}>
              <span class="ww-tooltip-icon" aria-hidden="true">
                ⓘ
              </span>
              <span class="ww-tooltip-bubble" role="tooltip">
                {tooltip}
              </span>
            </span>
          )}
        </span>
      )}
      <input
        type="number"
        class={inputCls}
        value={value === "" ? "" : String(value)}
        min={min}
        max={max}
        step={step}
        onInput={handleInput}
        {...rest}
      />
    </label>
  );
};
